/**
 * 菜单管理模块 - 完整的CRUD功能
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Input, Select, Space, Modal, message, Form, TreeSelect, Tag, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { menuService } from '../../../../../services/menuService';
import { eventBus } from '../../../../../utils/eventBus';
import QueryArea, { QueryField } from '../../components/QueryArea';
import IconSelector from '../../components/IconSelector';
import { processMenuData } from '../../../../../utils/simpleMenuProcessor';
import './Menu.css';


interface Menu {
  id: number;
  menuName: string;
  parentId: number;
  orderNum: number;
  path: string;
  component: string;
  query: string;
  isFrame: number;
  isCache: number;
  menuType: string;
  visible: number;
  status: number;
  perms: string;
  icon: string;
  createTime: string;
  children?: Menu[];
}

const MenuManagement: React.FC = () => {
  const [dataSource, setDataSource] = useState<Menu[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    menuType: '',
    status: '',
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // 移除展开/收缩功能
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Menu | null>(null);
  const [parentMenuOptions, setParentMenuOptions] = useState<Menu[]>([]);
  const [iconSelectorVisible, setIconSelectorVisible] = useState(false);

  // 查询字段配置
  const queryFields: QueryField[] = [
    {
      key: 'keyword',
      label: '菜单名',
      type: 'input',
      placeholder: '搜索菜单名称...',
      width: 200,
    },
    {
      key: 'menuType',
      label: '菜单类型',
      type: 'select',
      placeholder: '选择菜单类型',
      width: 150,
      options: [
        { label: '目录', value: 'M' },
        { label: '菜单', value: 'C' },
        { label: '按钮', value: 'F' },
      ],
    },
    {
      key: 'status',
      label: '状态',
      type: 'select',
      placeholder: '选择状态',
      width: 120,
      options: [
        { label: '正常', value: '1' },
        { label: '禁用', value: '0' },
      ],
    },
  ];

  // 表格列定义 - 简化版本
  const columns = [
    {
      title: '菜单名称',
      dataIndex: 'menuName',
      key: 'menuName',
      width: 200,
      render: (text: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: '路由地址',
      dataIndex: 'path',
      key: 'path',
      width: 200,
      render: (path: string) => path || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number) => (
        <Tag color={status === 1 ? 'success' : 'default'}>
          {status === 1 ? '正常' : '停用'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'orderNum',
      key: 'orderNum',
      width: 80,
      render: (orderNum: number) => orderNum || 0,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (text: any, record: any) => (
        <Space size="small">
          <Button 
            type="link"
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            修改
          </Button>
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleAddChild(record)}
          >
            新增
          </Button>
          <Button 
            type="link"
            size="small"
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 构建树形数据（用于Table的expandable功能）
  const buildTreeData = (data: Menu[]): any[] => {
    // 严格去重处理 - 按ID去重
    const uniqueMenus = Array.from(new Map(data.map(menu => [menu.id, menu])).values());
    
    // 创建菜单映射
    const menuMap = new Map<number, Menu & { children: Menu[] }>();
    const roots: (Menu & { children: Menu[] })[] = [];

    // 初始化所有节点
    uniqueMenus.forEach(menu => {
      menuMap.set(menu.id, { ...menu, children: [] });
    });

    // 构建树形结构
    uniqueMenus.forEach(menu => {
      const node = menuMap.get(menu.id)!;
      if (menu.parentId === 0) {
        // 根节点 - 检查是否已存在
        if (!roots.find(root => root.id === node.id)) {
          roots.push(node);
        }
      } else {
        // 子节点
        const parent = menuMap.get(menu.parentId);
        if (parent) {
          // 检查是否已存在
          if (!parent.children.find(child => child.id === node.id)) {
            parent.children.push(node);
          }
        }
      }
    });

    // 按orderNum排序
    const sortNodes = (nodes: Menu[]) => {
      nodes.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          sortNodes(node.children);
        }
      });
    };

    sortNodes(roots);

    // 转换为Table需要的格式
    const convertToTableFormat = (nodes: Menu[]): any[] => {
      return nodes.map(node => ({
        ...node,
        key: node.id.toString(),
        children: node.children && node.children.length > 0 ? convertToTableFormat(node.children) : undefined,
      }));
    };

    const treeData = convertToTableFormat(roots);
    return treeData;
  };

  // 获取所有有子节点的菜单key
  const getAllParentKeys = useCallback((nodes: any[]): React.Key[] => {
    const keys: React.Key[] = [];
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        // 使用node.id作为key，因为表格的rowKey设置为"id"
        keys.push(node.id);
        // 递归获取所有子节点的父级key
        const childKeys = getAllParentKeys(node.children);
        keys.push(...childKeys);
      }
    });
    return keys;
  }, []);

  // 数据加载
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...searchParams,
      };

      // 获取所有菜单数据，不分页
      const response = await menuService.getList({ ...params, current: 1, size: 1000 });
      if (response.code === 200) {
        const menus = response.data.records || response.data || [];
        
        // 调试输出原始数据
        menus.forEach((menu: Menu) => {
        });
        
        // 构建树形数据（用于展开功能）
        const treeData = buildTreeData(menus);
        
        setDataSource(menus);
        setTableData(treeData); // 直接使用树形数据
        setTreeData(treeData);
        
        // 手动设置展开状态
        const allParentKeys = getAllParentKeys(treeData);
        setExpandedRowKeys(allParentKeys);
        
        // 构建父级菜单选项（用于新增/编辑时的父级选择）
        setParentMenuOptions(menus);
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [searchParams, getAllParentKeys]);

  useEffect(() => {
    loadData();
  }, [searchParams]);

  // 确保在数据加载完成后立即设置展开状态
  useEffect(() => {
    if (treeData.length > 0) {
      const allParentKeys = getAllParentKeys(treeData);
      setExpandedRowKeys(allParentKeys);
      
      // 延迟设置，确保组件完全渲染
      setTimeout(() => {
        setExpandedRowKeys(allParentKeys);
      }, 100);
    }
  }, [treeData, getAllParentKeys]);

  // 搜索
  const handleSearch = () => {
    loadData();
  };

  // 重置
  const handleReset = () => {
    setSearchParams({ keyword: '', menuType: '', status: '' });
    setTimeout(loadData, 0);
  };


  // 处理查询参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams({
      keyword: values.keyword || '',
      menuType: values.menuType || '',
      status: values.status || '',
    });
  }, []);

  // 新增
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // 编辑
  const handleEdit = (record: Menu) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  // 移除展开/折叠处理功能

  // 新增子菜单
  const handleAddChild = (parentRecord: Menu) => {
    setEditingRecord(null);
    form.setFieldsValue({
      parentId: parentRecord.id,
      orderNum: 0,
      isFrame: 1,
      isCache: 0,
      visible: 1,
      status: 1,
      menuType: 'C',
    });
    setIsModalVisible(true);
  };

  // 删除
  const handleDelete = (id: number) => {
    if (id === 1) {
      message.error('不能删除根菜单');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个菜单吗？删除后无法恢复。',
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await menuService.delete(id);
          if (response.code === 200) {
            message.success('删除成功');
            loadData();
            eventBus.emit('menu:changed');
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }

    const hasRoot = selectedRowKeys.includes('1');
    if (hasRoot) {
      message.error('选中的菜单中包含根菜单，不能删除');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个菜单吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await menuService.batchDelete(selectedRowKeys.map(key => Number(key)));
          if (response.code === 200) {
            message.success('批量删除成功');
            setSelectedRowKeys([]);
            loadData();
            eventBus.emit('menu:changed');
          } else {
            message.error(response.message || '批量删除失败');
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 表单提交
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // 验证菜单名称
      if (!values.menuName || values.menuName.trim() === '') {
        message.error('请输入菜单名称');
        return;
      }
      
      // 验证路径
      if (!values.path || values.path.trim() === '') {
        message.error('请输入菜单路径');
        return;
      }
      
      // 验证路径格式
      if (!values.path.startsWith('/')) {
        message.error('菜单路径必须以 / 开头');
        return;
      }
      
      let response;
      if (editingRecord) {
        response = await menuService.update({ ...values, id: editingRecord.id });
      } else {
        response = await menuService.create(values);
      }
      
      if (response.code === 200) {
        message.success(editingRecord ? '编辑成功' : '新增成功');
        setIsModalVisible(false);
        loadData();
        eventBus.emit('menu:changed');
      } else {
        message.error(response.message || '提交失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      if (error && typeof error === 'object' && 'errorFields' in error) {
        message.error('请检查表单数据');
      } else {
      message.error('提交失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="menu-management">
      {/* 统一查询区域 */}
      <QueryArea
        fields={queryFields}
        values={searchParams}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleAdd}
        onBatchDelete={handleBatchDelete}
        batchDeleteDisabled={selectedRowKeys.length === 0}
        selectedCount={selectedRowKeys.length}
        addButtonText="新增菜单"
        batchDeleteButtonText="批量删除"
      />


      {/* 数据表格 */}
      <div className="table-card">
        <Table
          columns={columns}
          dataSource={treeData}
          loading={loading}
          rowKey="id"
          pagination={false}
          size="middle"
          scroll={{ x: 800 }}
          rowSelection={{
            selectedRowKeys: selectedRowKeys,
            onChange: setSelectedRowKeys,
            type: 'checkbox',
          }}
          expandable={{
            expandedRowKeys: expandedRowKeys, // 使用手动设置的展开状态
            expandRowByClick: false, // 禁用点击行展开
            onExpandedRowsChange: (keys) => {
              setExpandedRowKeys([...keys]);
            },
          }}
        />
      </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑菜单' : '新增菜单'}
        open={isModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          initialValues={{
            parentId: 0,
            orderNum: 0,
            isFrame: 1,
            isCache: 0,
            visible: 1,
            status: 1,
            menuType: 'C',
          }}
        >
          {/* 第一排：菜单名称和菜单类型 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="menuName"
                label="菜单名称:"
                rules={[{ required: true, message: '请输入菜单名称' }]}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <Input placeholder="请输入菜单名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="menuType"
                label="菜单类型:"
                rules={[{ required: true, message: '请选择菜单类型' }]}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Select placeholder="请选择菜单类型">
                  <Select.Option value="M">目录</Select.Option>
                  <Select.Option value="C">菜单</Select.Option>
                  <Select.Option value="F">按钮</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 第二排：上级菜单 */}
          <Form.Item
            name="parentId"
            label="上级菜单:"
            labelAlign="left"
          >
            <TreeSelect
              treeData={parentMenuOptions}
              placeholder="请选择上级菜单"
              treeDefaultExpandAll
              fieldNames={{ label: 'menuName', value: 'id', children: 'children' }}
            />
          </Form.Item>

          {/* 第三排：路由地址和组件路径 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="path"
                label="路由地址:"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <Input placeholder="请输入路由地址" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="component"
                label="组件路径:"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Input placeholder="请输入组件路径" />
              </Form.Item>
            </Col>
          </Row>

          {/* 第四排：权限标识和菜单图标 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="perms"
                label="权限标识:"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <Input placeholder="如：system:user:list" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="icon"
                label="菜单图标:"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Input.Group compact>
                  <Input 
                    placeholder="请选择菜单图标" 
                    readOnly
                    value={form.getFieldValue('icon') ? '已选择图标' : ''}
                    style={{ width: 'calc(100% - 80px)' }}
                  />
                  <Button 
                    type="default"
                    onClick={() => setIconSelectorVisible(true)}
                    style={{ width: 80 }}
                  >
                    选择图标
                  </Button>
                </Input.Group>
              </Form.Item>
            </Col>
          </Row>

          {/* 第五排：显示排序和菜单状态 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="orderNum"
                label="显示排序:"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <Input placeholder="请输入显示排序" type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="菜单状态:"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Select>
                  <Select.Option value={1}>正常</Select.Option>
                  <Select.Option value={0}>停用</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 第六排：显示状态和是否缓存 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="visible"
                label="显示状态:"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <Select>
                  <Select.Option value={1}>显示</Select.Option>
                  <Select.Option value={0}>隐藏</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isCache"
                label="是否缓存:"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Select>
                  <Select.Option value={1}>缓存</Select.Option>
                  <Select.Option value={0}>不缓存</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 图标选择器 */}
      <IconSelector
        visible={iconSelectorVisible}
        onClose={() => setIconSelectorVisible(false)}
        onSelect={(icon) => {
          form.setFieldsValue({ icon });
          setIconSelectorVisible(false);
        }}
        currentIcon={(() => {
          const currentIconValue = form.getFieldValue('icon');
          return currentIconValue;
        })()}
      />
    </div>
  );
};

export default MenuManagement;












